import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Table as TRTable } from '@tanstack/react-table';
import { ButtonDropdown, TableV2 } from '@anytag/shared-atoms';
import { AllCampaignsReportSummary, useAllCampaignsReportQuery } from '@anytag/graphql';
import { convertOrderToORDER, getOffset, getRestApiUrl, getValuableVariables } from '@anytag/shared-utils';
import { useDownloadFile } from '@anytag/shared-hooks';
import { GTM_IDS } from '@anytag/shared-constants';
import { DownloadFileType } from '@anytag/shared-types';
import { useReportFilter } from '../report-filter';
import { formatReportData, getReportQueryVariables } from './utils';
import { useReportTable } from './hooks';
import { ReportColumnKeys, ReportCsvDownloadKeys, ReportRowData } from './types';

export interface ReportTableProps {
  summaryData?: AllCampaignsReportSummary;
}

export const ReportTable = ({ summaryData }: ReportTableProps) => {
  const { t } = useTranslation();
  const tableRef = useRef<TRTable<ReportRowData>>(null);
  const { filter, page, limit, sort } = useReportFilter();
  const { downloading, handleDownloadFile } = useDownloadFile();
  const { data, previousData, loading } = useAllCampaignsReportQuery({
    fetchPolicy: 'no-cache',
    variables: getValuableVariables({
      limit,
      offset: getOffset(page, limit),
      orderBy: { field: sort.field, order: convertOrderToORDER(sort.order) },
      ...getReportQueryVariables(filter)
    })
  });
  const { columns } = useReportTable({ summaryData });

  const reportData = (loading ? previousData?.allCampaignsReport : data?.allCampaignsReport) || [];

  const handleDownloadReport = async (fileType?: DownloadFileType) => {
    // Only support download CSV for now
    if (fileType === DownloadFileType.CSV) {
      const unsupportedDownloadColumns: Partial<ReportColumnKeys>[] = [
        'sales',
        'retweets', // share
        'conversion',
        'averageViewDurationMinutes'
      ];
      const visibleColumns = tableRef.current?.getVisibleFlatColumns();
      const selectedColumns = visibleColumns?.reduce<ReportCsvDownloadKeys[]>((results, curr) => {
        if (
          curr.columnDef.meta?.sortField &&
          curr.id &&
          !unsupportedDownloadColumns.includes(curr.id as ReportColumnKeys)
        ) {
          results.push(curr.id as ReportCsvDownloadKeys);
        }

        return results;
      }, []);

      await handleDownloadFile({
        filenamePrefix: 'report_campaign',
        url: getRestApiUrl('report/campaign/csv/'),
        params: {
          ...sort,
          selectedColumns,
          endDate: filter.endDate,
          campaigns: filter.campaigns,
          countryId: filter.countryId,
          startDate: filter.startDate,
          socialMedias: filter.socialMedias,
          campaignTypes: filter.campaignTypes,
          campaignCategoryIds: filter.campaignCategoryIds
        }
      });
    }
  };

  return (
    <>
      <div className="flex justify-end border-t-border bg-white px-4 py-2">
        <ButtonDropdown
          variant="outline"
          title={t('Button.Download')}
          className="min-w-33 uppercase"
          prefixIcon={{ icon: 'download', size: '14px' }}
          loading={{ showIcon: true, status: downloading }}
          onSelectOption={({ value }) => handleDownloadReport(value)}
          options={[
            {
              label: t<string>('CSV'),
              value: DownloadFileType.CSV,
              gtmId: GTM_IDS.report.csv_download
            }
          ]}
        />
      </div>

      <TableV2
        rowHeight="73px"
        maxHeight="820px"
        loading={loading}
        columns={columns}
        className="report-table"
        reactTableRef={tableRef}
        data={formatReportData(reportData, summaryData?.summary)}
        hasColumnCustomization
        noTopBorderRadius
        stickyHeader
        hasBorder
      />
    </>
  );
};
